import React, { useSyncExternalStore } from 'react'
import * as THREE from 'three'
import { Helper } from '@react-three/drei'
import ShadowSunLight from './ShadowSunLight'
import SpotlightRig from './SpotlightRig'
import TorchLight from './TorchLight'
import { sphericalToXYZ } from '../lib/lightPlacement'
import { kelvinToHex } from '../lib/colorTemperature'
import { sceneSelection } from '../lib/sceneSelection'
import { getTargetTier, subscribe as subscribeTextureQuality } from '../lib/textureQuality'
import { effectiveShadowMapSize } from '../lib/shadowQuality'

// SceneLights — mounts whatever `liveTuneStore.lights` holds (ENG-1190).
//
// Before this the scene had one JSX block per fixed slot: the sun read off
// `shadow`, three <SpotlightRig>s off `spotlight`/`spotlight2`/`spotlight3`, a
// <TorchLight> off `torch`, a <hemisphereLight> off `hemisphere`. Lights are
// objects now — added and removed from the outliner — so there is one mount
// that walks the map and picks a renderer by `type`. The per-type components
// are the ones that existed; only who instantiates them changed.
//
// Two rules carried over verbatim, because each was a measured cost:
//   - A directional mounts only with an environment (`hasEnv`). Its VSM shadow
//     pass is the single most expensive thing in a software-rendered frame, and
//     the no-env studio rig's em_key already casts the authoring shadow — both
//     at once blew CI's boot budget. The outliner row says so when it is inert.
//   - The sun orbits (and now aims at) the live HEAD PROBE (stage floor + head
//     height), not the origin, so a splat whose floor sits at y=-6 keeps the
//     sun on the subject.
//
// Selection: the light whose outliner row is selected (sceneSelection's
// selectedId is the light id — the adapter id) draws its helper, the way
// Unreal shows a selected light's cone / arrow / radius.
//
// Globals: the legacy ids keep the names tests and the console know —
// `spot1` → __SPOTLIGHT__ / __SPOTLIGHT_TARGET__ (spot2/spot3 the _2/_3
// variants), `torch` → __TORCH__ (CLAUDE.md forbids renaming it). Every light
// also publishes under window.__LIGHTS__[id] — see SpotlightRig / TorchLight.

const LEGACY_SPOT_GLOBALS = {
  spot1: ['__SPOTLIGHT__', '__SPOTLIGHT_TARGET__'],
  spot2: ['__SPOTLIGHT_2__', '__SPOTLIGHT_TARGET_2__'],
  spot3: ['__SPOTLIGHT_3__', '__SPOTLIGHT_TARGET_3__'],
}

// three's HemisphereLight has ONE intensity for both colours; independent
// sky/ground intensities are faked by pre-multiplying each colour.
function scaledColor(hex, intensity) {
  return new THREE.Color(hex).multiplyScalar(intensity)
}

const getSelectedId = () => sceneSelection.getState().selectedId

export default function SceneLights({ lights, hasEnv, headProbe }) {
  const selectedId = useSyncExternalStore(sceneSelection.subscribe, getSelectedId, getSelectedId)
  // This viewer's scalability tier CAPS every authored shadow map (ENG-1230).
  // Subscribed, not read once: the tier changes when someone picks a quality in
  // the menu and when `auto` re-decides from measured bandwidth, and a shadow
  // map that only followed on reload would look like the setting did nothing.
  // A string snapshot, so useSyncExternalStore's identity check is stable.
  const tier = useSyncExternalStore(subscribeTextureQuality, getTargetTier, getTargetTier)
  if (!lights) return null
  return (
    <>
      {Object.entries(lights).map(([id, l]) => {
        if (!l) return null
        const selected = selectedId === id
        switch (l.type) {
          case 'directional':
            if (!hasEnv || !l.enabled) return null
            return (
              <ShadowSunLight
                key={id}
                lightPos={sphericalToXYZ(l.distance, l.elevation, l.azimuth, headProbe)}
                aim={headProbe}
                intensity={l.intensity}
                color={kelvinToHex(l.temperature)}
                mapSize={effectiveShadowMapSize(l.shadowMapSize, tier)}
                cameraSize={l.shadowCameraSize}
                radius={l.shadowRadius}
                blurSamples={l.shadowBlurSamples}
                bias={l.shadowBias}
                selected={selected}
              />
            )
          case 'spot': {
            const [lightGlobal, targetGlobal] = LEGACY_SPOT_GLOBALS[id] ?? []
            return (
              <SpotlightRig
                key={id} id={id} tune={l}
                shadowMapSize={effectiveShadowMapSize(l.shadowMapSize, tier)}
                lightGlobal={lightGlobal} targetGlobal={targetGlobal} selected={selected}
              />
            )
          }
          case 'point':
            return <TorchLight key={id} id={id} torch={l} selected={selected} />
          case 'hemisphere':
            if (!l.enabled) return null
            return (
              <hemisphereLight
                key={id}
                // three takes the sky direction from the light's position.
                position={sphericalToXYZ(1, l.elevation ?? 90, l.azimuth ?? 0, [0, 0, 0])}
                color={scaledColor(l.skyColor, l.skyIntensity)}
                groundColor={scaledColor(l.groundColor, l.groundIntensity)}
              >
                {selected && <Helper type={THREE.HemisphereLightHelper} args={[0.4]} />}
              </hemisphereLight>
            )
          default:
            return null
        }
      })}
    </>
  )
}
